import React, { useState, useEffect } from "react";
import {
  Server,
  Shield,
  Users,
  Database,
  Globe,
  Cpu,
  ArrowRight,
  CheckCircle,
  Sparkles,
  Zap,
  TrendingUp,
  Award,
  Clock,
  ChevronRight
} from "lucide-react";

// Animasyonlu Değer Bileşeni
const AnimatedValue = ({ value, duration = 2000, decimals = 0, suffix = "+" }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const steps = 60;
    const increment = value / steps;
    const interval = duration / steps;

    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(current);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [value, duration]);

  return (
    <span className="font-bold">
      {decimals === 0
        ? Math.floor(count).toLocaleString()
        : count.toFixed(decimals)}
      {suffix}
    </span>
  );
};

// İstatistik Kartı
const IstatistikKarti = ({
  icon: Icon,
  value,
  label,
  suffix = "+",
  decimals = 0,
  color = "from-blue-500 to-purple-500"
}) => (
  <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-950/90 backdrop-blur-sm rounded-2xl p-6 border border-gray-800 hover:border-blue-600/50 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-600/20 overflow-hidden">
    {/* Arkaplan Efektleri */}
    <div className={`absolute inset-0 bg-gradient-to-r ${color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.1),transparent_50%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    
    {/* Parlama Efekti */}
    <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/20 to-purple-600/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

    <div className="relative">
      <div className="flex items-center space-x-3 mb-4">
        <div className={`p-2 rounded-lg bg-gradient-to-r ${color} group-hover:scale-110 transition-transform duration-500`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <span className="text-sm font-medium text-gray-400 group-hover:text-gray-300 transition-colors">
          {label}
        </span>
      </div>
      
      <div className="text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent group-hover:from-blue-400 group-hover:to-purple-400 transition-all duration-500">
        <AnimatedValue value={value} decimals={decimals} suffix={suffix} />
      </div>
    </div>
  </div>
);

// Özellik Kartı
const OzellikKarti = ({ icon: Icon, title, description, color = "from-blue-500 to-purple-500" }) => (
  <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-950/90 backdrop-blur-sm rounded-2xl p-8 border border-gray-800 hover:border-blue-600/50 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-600/20 overflow-hidden">
    {/* Hover Gradient */}
    <div className={`absolute inset-0 bg-gradient-to-r ${color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
    
    {/* Işık Efekti */}
    <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/10 to-purple-600/10 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    
    {/* Dekoratif Çizgi */}
    <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`} />

    <div className="relative">
      <div className="flex items-center justify-between mb-6">
        <div className={`p-3 rounded-xl bg-gradient-to-r ${color} group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <Sparkles size={20} className="text-blue-500/30 group-hover:text-blue-500/50 transition-colors" />
      </div>
      
      <h3 className="text-xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-purple-400 transition-all duration-500">
        {title}
      </h3>
      
      <p className="text-gray-400 text-sm leading-relaxed group-hover:text-gray-300 transition-colors">
        {description}
      </p>

      <div className="mt-6 flex items-center text-xs text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
        <CheckCircle size={12} className="mr-1" />
        Hemen başlayın
      </div>
    </div>
  </div>
);

// Müşteri Logosu
const MusteriLogosu = ({ name }) => (
  <div className="px-6 py-3 bg-gray-800/30 backdrop-blur-sm rounded-xl border border-gray-800 hover:border-blue-600/30 transition-all duration-300">
    <span className="text-gray-400 text-sm font-medium">{name}</span>
  </div>
);

export default function HeroSection() {
  const stats = [
    { icon: Server, value: 3, label: "Veri Merkezi", suffix: "+", color: "from-blue-500 to-cyan-500" },
    { icon: Globe, value: 2, label: "Küresel Konum", suffix: "+", color: "from-purple-500 to-pink-500" },
    { icon: Shield, value: 2.7, label: "DDoS Koruma", suffix: " Tbps", decimals: 1, color: "from-green-500 to-emerald-500" },
    { icon: Database, value: 99.9, label: "Çalışma Süresi", suffix: "%", decimals: 1, color: "from-orange-500 to-red-500" },
    { icon: Zap, value: 10, label: "Kurulum Süresi", suffix: " dk", color: "from-yellow-500 to-amber-500" },
    { icon: TrendingUp, value: 3500, label: "Aktif Sunucu", suffix: "+", color: "from-indigo-500 to-blue-500" },
    { icon: Users, value: 2500, label: "Mutlu Müşteri", suffix: "+", color: "from-pink-500 to-rose-500" },
    { icon: Award, value: 99.9, label: "Müşteri Memnuniyeti", suffix: "%", decimals: 1, color: "from-cyan-500 to-blue-500" }
  ];

  return (
    <section className="relative min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-black overflow-hidden">
      {/* Arkaplan Katmanları */}
      <div className="absolute inset-0">
        {/* Grid Deseni */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(59, 130, 246, 0.1) 1px, transparent 0)`,
            backgroundSize: "40px 40px"
          }}
        />

        {/* Gradient Orbs */}
        <div className="absolute top-0 -left-1/4 w-1/2 h-1/2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 -right-1/4 w-1/2 h-1/2 bg-gradient-to-l from-indigo-600/20 to-pink-600/20 rounded-full blur-3xl animate-pulse" />
        
        {/* Parçacık Efektleri */}
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-blue-500/20 rounded-full"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 5}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 py-24 z-10">
        {/* Rozet */}
        <div className="flex justify-center mb-8 animate-fadeInDown">
          <div className="inline-flex items-center bg-gradient-to-r from-gray-900/90 to-gray-950/90 backdrop-blur-sm rounded-full p-1 border border-gray-800 hover:border-blue-600/50 transition-all duration-500 group">
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-full px-4 py-2 text-sm font-medium text-white">
              Yeni Nesil
            </span>
            <div className="flex items-center space-x-2 px-4">
              <Sparkles size={16} className="text-blue-500 group-hover:rotate-12 transition-transform" />
              <span className="text-gray-300 text-sm font-medium">
                Kurumsal Hosting Platformu
              </span>
            </div>
          </div>
        </div>

        {/* Ana Başlık */}
        <div className="text-center mb-12 animate-fadeInUp">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">
              Kurumsal Altyapınız
            </span>
            <br />
            <span className="text-white">için Güçlü ve Güvenilir Çözümler</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto font-light leading-relaxed">
            Kurumsal düzeyde donanımla desteklenen yüksek performanslı sunucuları 
            <span className="text-blue-500 font-semibold"> birkaç tıkla</span> hızlıca kurun
          </p>

          {/* CTA Butonları */}
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <a
              href="/virtual-server"
              className="group relative bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-10 py-5 rounded-xl transition-all duration-500 hover:scale-105 font-semibold flex items-center shadow-2xl shadow-blue-600/25 hover:shadow-blue-600/40 overflow-hidden"
            >
              <span className="absolute inset-0 bg-white/20 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700" />
              <Server size={20} className="mr-3 group-hover:rotate-12 transition-transform" />
              Sunucu Kur
              <ArrowRight size={18} className="ml-3 group-hover:translate-x-2 transition-transform" />
            </a>
            
            <a
              href="/contact"
              className="group bg-gray-900/90 backdrop-blur-sm hover:bg-gray-800/90 text-white px-10 py-5 rounded-xl transition-all duration-500 hover:scale-105 font-semibold border border-gray-800 hover:border-blue-600/50 flex items-center"
            >
              <span className="mr-3">Bize Ulaşın</span>
              <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>

        {/* İstatistik Izgarası */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {stats.slice(0, 4).map((stat, index) => (
            <div key={index} className="animate-fadeIn" style={{ animationDelay: `${0.2 + index * 0.1}s` }}>
              <IstatistikKarti {...stat} />
            </div>
          ))}
        </div>

        {/* Güven Rozeti */}
        <div className="text-center animate-fadeIn" style={{ animationDelay: "0.9s" }}>
          <div className="inline-flex items-center space-x-2 bg-blue-600/10 backdrop-blur-sm px-6 py-3 rounded-full border border-blue-600/30 mb-6">
            <Shield size={16} className="text-blue-500" />
            <span className="text-sm text-gray-300">
              ISO 27001 Sertifikalı • Tier III Veri Merkezi • %99.9 Uptime Garantisi
            </span>
          </div>
        </div>
      </div>

      {/* CSS Animasyonları */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
          opacity: 0;
        }
        
        .animate-fadeInDown {
          animation: fadeInDown 0.6s ease-out forwards;
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out forwards;
        }
        
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}